/**
 * Credit / Liabilities Tab — Bureau credit scores (editable), median score, liabilities table, key ratios.
 * Consumes API shape: { loan: LoanInfoViewModel, borrowers: BorrowerViewModel[] }
 * Liabilities are loaded independently from the liabilities API.
 */
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useFormContext, useWatch } from 'react-hook-form';
import { Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress, Grid, IconButton, MenuItem, Stack, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';
import SummarizeIcon from '@mui/icons-material/Summarize';
import loanLiabilitiesApi from '../../../services/loanLiabilitiesApi';
import CurrencyInput from '../../../components/shared/CurrencyInput';
import SectionHeader from '../../../components/shared/SectionHeader';
import { getAxiosErrorMessage } from '../../../utils/getAxiosErrorMessage';

import { fmtDollar as fmt, fmtPct as pct } from '../../../utils/formatters';

const BUREAUS = [
  { key: 'Equifax', label: 'Equifax', color: 'error' },
  { key: 'Experian', label: 'Experian', color: 'primary' },
  { key: 'TransUnion', label: 'TransUnion', color: 'info' },
];

const LIABILITY_TYPES = [
  { value: 'RevolvingCreditLine', label: 'Revolving Credit' },
  { value: 'UnsecuredInstallment', label: 'Unsecured Installment' },
  { value: 'SecuredInstallment', label: 'Secured Installment' },
  { value: 'Mortgage', label: 'Mortgage' },
  { value: 'HELOC', label: 'HELOC' },
  { value: 'HomeEquityLoan', label: 'Home Equity Loan' },
  { value: 'CollectionsJudgmentsAndLiens', label: 'Collections / Judgments' },
  { value: 'LeasePayment', label: 'Lease Payment' },
  { value: 'Open30DayChargeAccount', label: '30-Day Charge Account' },
  { value: 'Taxes', label: 'Taxes' },
  { value: 'TaxLien', label: 'Tax Lien' },
  { value: 'PACELoan', label: 'PACE Loan' },
  { value: 'Alimony', label: 'Alimony' },
  { value: 'ChildSupport', label: 'Child Support' },
  { value: 'DeferredStudentLoan', label: 'Deferred Student Loan' },
  { value: 'EquityBuyout', label: 'Equity Buyout' },
  { value: 'OtherLiability', label: 'Other' },
];

const scoreMuiColor = (score) => {
  if (score == null) return 'text.secondary';
  if (score >= 740) return 'success.main';
  if (score >= 670) return 'warning.main';
  return 'error.main';
};

const scoreChipColor = (score) => {
  if (score == null) return 'default';
  if (score >= 740) return 'success';
  if (score >= 670) return 'warning';
  return 'error';
};

const CreditLiabilitiesTab = ({ loanDetail, isEditMode = false }) => {
  const { id: loanId } = useParams();
  const { control, setValue } = useFormContext();
  const watchedBorrowers = useWatch({ control, name: 'borrowers' });
  const { data: liabilitiesData, isLoading: liabilitiesLoading } = loanLiabilitiesApi.endpoints.getLoanLiabilities.useQuery(loanId, { skip: !loanId });
  const [updateLiability] = loanLiabilitiesApi.endpoints.updateLoanLiability.useMutation();
  const [createLiability] = loanLiabilitiesApi.endpoints.createLoanLiability.useMutation();
  const [deleteLiability] = loanLiabilitiesApi.endpoints.deleteLoanLiability.useMutation();

  // Local edit state for liabilities (keyed by liabilityId or temp index)
  const [editedLiabilities, setEditedLiabilities] = useState({});
  const [newLiabilities, setNewLiabilities] = useState([]);
  const [savingIds, setSavingIds] = useState(new Set());
  const [liabilityErrors, setLiabilityErrors] = useState({});

  if (!loanDetail) return null;
  const l = loanDetail.loan || {};
  const borrowers = (isEditMode ? watchedBorrowers : loanDetail.borrowers) || [];
  const liabilities = liabilitiesData?.liabilities || [];
  const totalLiabilityPayment = liabilities.reduce((s, li) => s + (li.monthlyPayment || 0), 0);
  const totalLiabilityBalance = liabilities.reduce((s, li) => s + (li.unpaidBalance || 0), 0);

  const getEditedValue = (liabilityId, field) => {
    return editedLiabilities[liabilityId]?.[field];
  };

  const getLiabilityValue = (li, field) => {
    const edited = getEditedValue(li.liabilityId, field);
    return edited !== undefined ? edited : li[field];
  };

  const handleLiabilityEdit = (liabilityId, field, value) => {
    setEditedLiabilities(prev => ({
      ...prev,
      [liabilityId]: { ...(prev[liabilityId] || {}), [field]: value },
    }));
    setLiabilityErrors(prev => { const u = { ...prev }; delete u[liabilityId]; return u; });
  };

  const handleSaveLiability = async (li) => {
    const edits = editedLiabilities[li.liabilityId];
    if (!edits || Object.keys(edits).length === 0) return;
    setSavingIds(prev => new Set(prev).add(li.liabilityId));
    try {
      await updateLiability({
        loanId,
        liabilityId: li.liabilityId,
        body: {
          liabilityType: edits.liabilityType ?? li.liabilityType,
          creditorName: edits.creditorName ?? li.creditorName,
          accountIdentifier: edits.accountIdentifier ?? li.accountIdentifier,
          unpaidBalance: edits.unpaidBalance !== undefined ? edits.unpaidBalance : li.unpaidBalance,
          monthlyPayment: edits.monthlyPayment !== undefined ? edits.monthlyPayment : li.monthlyPayment,
          monthsRemaining: edits.monthsRemaining !== undefined ? edits.monthsRemaining : li.monthsRemaining,
          excludedFromDti: edits.excludedFromDti !== undefined ? edits.excludedFromDti : li.excludedFromDti,
          toBePaidOff: edits.toBePaidOff !== undefined ? edits.toBePaidOff : li.toBePaidOff,
        },
      }).unwrap();
      setEditedLiabilities(prev => { const u = { ...prev }; delete u[li.liabilityId]; return u; });
    } catch (err) {
      setLiabilityErrors(prev => ({ ...prev, [li.liabilityId]: getAxiosErrorMessage(err, 'Failed to save.') }));
    } finally {
      setSavingIds(prev => { const u = new Set(prev); u.delete(li.liabilityId); return u; });
    }
  };

  const handleDeleteLiability = async (li) => {
    setSavingIds(prev => new Set(prev).add(li.liabilityId));
    try {
      await deleteLiability({ loanId, liabilityId: li.liabilityId }).unwrap();
      setEditedLiabilities(prev => { const u = { ...prev }; delete u[li.liabilityId]; return u; });
    } catch (err) {
      setLiabilityErrors(prev => ({ ...prev, [li.liabilityId]: getAxiosErrorMessage(err, 'Failed to delete.') }));
    } finally {
      setSavingIds(prev => { const u = new Set(prev); u.delete(li.liabilityId); return u; });
    }
  };

  const handleNewLiabilityChange = (idx, field, value) => {
    setNewLiabilities(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const handleAddNewRow = () => {
    setNewLiabilities(prev => [...prev, { liabilityType: '', creditorName: '', accountIdentifier: '', unpaidBalance: null, monthlyPayment: null, monthsRemaining: null }]);
  };

  const handleSaveNewLiability = async (idx) => {
    const nl = newLiabilities[idx];
    const tempId = `new-${idx}`;
    setSavingIds(prev => new Set(prev).add(tempId));
    try {
      await createLiability({ loanId, body: nl }).unwrap();
      setNewLiabilities(prev => prev.filter((_, i) => i !== idx));
    } catch (err) {
      setLiabilityErrors(prev => ({ ...prev, [tempId]: getAxiosErrorMessage(err, 'Failed to create.') }));
    } finally {
      setSavingIds(prev => { const u = new Set(prev); u.delete(tempId); return u; });
    }
  };

  const handleRemoveNewRow = (idx) => {
    setNewLiabilities(prev => prev.filter((_, i) => i !== idx));
  };

  const getBureauScore = (borrower, bureauKey) => {
    const scores = borrower?.creditScores || [];
    const match = scores.find(s => s.bureau === bureauKey);
    return match?.score ?? null;
  };

  const handleScoreChange = (borrowerIndex, bureauKey, value) => {
    const raw = borrowers?.[borrowerIndex]?.creditScores;
    const currentScores = Array.isArray(raw)
      ? raw.map(s => ({ bureau: s.bureau, score: s.score }))
      : [];
    const scoreVal = value === '' || value == null ? null : (Number.parseInt(String(value), 10) || null);

    let nextScores;
    if (scoreVal == null) {
      nextScores = currentScores.filter(s => s.bureau !== bureauKey);
    } else if (currentScores.some(s => s.bureau === bureauKey)) {
      nextScores = currentScores.map(s =>
        s.bureau === bureauKey ? { bureau: bureauKey, score: scoreVal } : { bureau: s.bureau, score: s.score }
      );
    } else {
      nextScores = [...currentScores, { bureau: bureauKey, score: scoreVal }];
    }

    setValue(`borrowers.${borrowerIndex}.creditScores`, nextScores, { shouldDirty: true });
  };

  const hasAnyScores = (borrower) => {
    return BUREAUS.some(b => getBureauScore(borrower, b.key) != null);
  };

  const showSidebar = !isEditMode;

  const creditSidebar = showSidebar ? (
    <Box sx={{ position: 'sticky', top: 16 }}>
      <Card variant="outlined" sx={{ bgcolor: 'background.default' }}>
        <CardContent sx={{ pb: '12px !important' }}>
          <SectionHeader title="Credit Snapshot" icon={<SummarizeIcon fontSize="small" />} sx={{ mb: 1 }} />

          {/* Per-borrower scores */}
          <Stack spacing={1.5}>
            {borrowers.map((borrower, bIdx) => (
              <Box key={borrower.borrowerId || bIdx}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <PersonIcon sx={{ fontSize: 16, color: 'action.active' }} />
                    <Typography variant="subtitle2" fontWeight={700}>{borrower.firstName} {borrower.lastName}</Typography>
                  </Stack>
                  <Typography variant="subtitle2" fontWeight={700} color={scoreMuiColor(borrower.creditScore)}>{borrower.creditScore ?? '—'}</Typography>
                </Stack>
                <Box sx={{ pl: 0.5 }}>
                  {BUREAUS.map(bureau => {
                    const score = getBureauScore(borrower, bureau.key);
                    return (
                      <Stack key={bureau.key} direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: `${bureau.color}.main` }} />
                          <Typography variant="caption" color="text.secondary">{bureau.label}</Typography>
                        </Stack>
                        <Typography variant="caption" fontWeight={600} color={scoreMuiColor(score)}>{score ?? '—'}</Typography>
                      </Stack>
                    );
                  })}
                </Box>
                {bIdx < borrowers.length - 1 && <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 1 }} />}
              </Box>
            ))}
          </Stack>

          {/* Liabilities summary */}
          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider', pl: 0.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>Liabilities</Typography>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
              <Typography variant="caption" color="text.secondary">Total Balance</Typography>
              <Typography variant="caption" fontWeight={700} color="error.main">{fmt(totalLiabilityBalance)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
              <Typography variant="caption" color="text.secondary">Monthly Payment</Typography>
              <Typography variant="caption" fontWeight={700} color="error.main">{fmt(totalLiabilityPayment)}/mo</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
              <Typography variant="caption" color="text.secondary"># of Accounts</Typography>
              <Typography variant="caption" fontWeight={600}>{liabilities.length}</Typography>
            </Stack>
          </Box>

          {/* Key Ratios */}
          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider', pl: 0.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>Key Ratios</Typography>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
              <Typography variant="caption" color="text.secondary">DTI</Typography>
              <Typography variant="caption" fontWeight={700} color={l.dtiRatio > 43 ? 'error.main' : l.dtiRatio > 36 ? 'warning.main' : 'success.main'}>{pct(l.dtiRatio)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
              <Typography variant="caption" color="text.secondary">LTV</Typography>
              <Typography variant="caption" fontWeight={700} color={l.ltvRatio > 80 ? 'warning.main' : 'success.main'}>{pct(l.ltvRatio)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">CLTV</Typography>
              <Typography variant="caption" fontWeight={700}>{pct(l.cltvRatio)}</Typography>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  ) : null;

  return (
    <Box>
      <Grid container spacing={2}>
        {/* Left column: Credit detail sections */}
        <Grid size={{ xs: 12, lg: showSidebar ? 8 : 12 }}>

      {/* Credit Scores per Borrower */}
      {borrowers.map((borrower, bIdx) => (
        <Box key={borrower.borrowerId || bIdx} sx={bIdx > 0 ? { mt: 4 } : undefined}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700}>Credit Scores — {borrower.firstName} {borrower.lastName}</Typography>
            {borrower.borrowerType && <Chip label={borrower.borrowerType} size="small" variant="outlined" color="info" />}
          </Stack>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Card variant="outlined" sx={{ textAlign: 'center' }}>
                <CardContent sx={{ py: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Median Score</Typography>
                  <Typography variant="h4" color={scoreMuiColor(borrower.creditScore)}>{borrower.creditScore ?? '—'}</Typography>
                  {borrower.creditScore != null && (
                    <Chip label={borrower.creditScore >= 740 ? 'Excellent' : borrower.creditScore >= 670 ? 'Good' : borrower.creditScore >= 580 ? 'Fair' : 'Poor'} size="small" color={scoreChipColor(borrower.creditScore)} variant="outlined" />
                  )}
                </CardContent>
              </Card>
            </Grid>
            {BUREAUS.map(bureau => {
              const score = getBureauScore(borrower, bureau.key);
              return (
                <Grid key={bureau.key} size={{ xs: 6, sm: 3 }}>
                  <Card variant="outlined" sx={{ textAlign: 'center' }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: `${bureau.color}.main` }} />
                        <Typography variant="caption" color="text.secondary">{bureau.label}</Typography>
                      </Stack>
                      {isEditMode ? (
                        <TextField type="number" size="small" value={score ?? ''} onChange={(e) => handleScoreChange(bIdx, bureau.key, e.target.value)} placeholder="—" slotProps={{ htmlInput: { min: 300, max: 850, style: { textAlign: 'center', fontWeight: 700 } } }} sx={{ mt: 0.5 }} />
                      ) : (
                        <Typography variant="h5" color={scoreMuiColor(score)} sx={{ mt: 0.5 }}>{score ?? '—'}</Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {!hasAnyScores(borrower) && !isEditMode && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>No credit scores on file.</strong> Pull credit or enter scores manually in edit mode to populate bureau scores. UFF will compute the median automatically once 3 scores are saved.
            </Alert>
          )}

          {isEditMode && (
            <Alert severity="warning" sx={{ mb: 2, py: 0.5 }}>Enter scores for all 3 bureaus. UFF will compute the median score on save.</Alert>
          )}
        </Box>
      ))}

      {/* Liabilities */}
      <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 4, mb: 2 }}>Liabilities</Typography>
      {liabilitiesLoading ? (
        <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={16} /><Typography variant="body2" color="text.secondary">Loading liabilities...</Typography></Stack>
      ) : !isEditMode && liabilities.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No liabilities recorded for this loan.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Creditor</TableCell>
              <TableCell>Account</TableCell>
              <TableCell align="right">Balance</TableCell>
              <TableCell align="right">Monthly Pmt</TableCell>
              <TableCell align="center">Mo. Remaining</TableCell>
              <TableCell align="center">Paid Off?</TableCell>
              {isEditMode && <TableCell sx={{ width: 80 }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {liabilities.map((li, i) => {
              const isSaving = savingIds.has(li.liabilityId);
              const hasEdits = !!editedLiabilities[li.liabilityId];
              const error = liabilityErrors[li.liabilityId];
              return isEditMode ? (
                <TableRow key={li.liabilityId || i}>
                  <TableCell>
                    <TextField select size="small" value={getLiabilityValue(li, 'liabilityType') || ''} onChange={(e) => handleLiabilityEdit(li.liabilityId, 'liabilityType', e.target.value)} sx={{ minWidth: 130 }}>
                      <MenuItem value="">Select...</MenuItem>
                      {LIABILITY_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell><TextField size="small" value={getLiabilityValue(li, 'creditorName') || ''} onChange={(e) => handleLiabilityEdit(li.liabilityId, 'creditorName', e.target.value)} placeholder="Creditor" /></TableCell>
                  <TableCell><TextField size="small" value={getLiabilityValue(li, 'accountIdentifier') || ''} onChange={(e) => handleLiabilityEdit(li.liabilityId, 'accountIdentifier', e.target.value)} placeholder="Acct #" /></TableCell>
                  <TableCell><CurrencyInput size="small" prefix="" value={getLiabilityValue(li, 'unpaidBalance') ?? ''} onChange={(v) => handleLiabilityEdit(li.liabilityId, 'unpaidBalance', typeof v === 'number' ? v : null)} placeholder="0" sx={{ '& input': { textAlign: 'right' } }} /></TableCell>
                  <TableCell><CurrencyInput size="small" prefix="" value={getLiabilityValue(li, 'monthlyPayment') ?? ''} onChange={(v) => handleLiabilityEdit(li.liabilityId, 'monthlyPayment', typeof v === 'number' ? v : null)} placeholder="0" sx={{ '& input': { textAlign: 'right' } }} /></TableCell>
                  <TableCell><TextField size="small" type="number" value={getLiabilityValue(li, 'monthsRemaining') ?? ''} onChange={(e) => handleLiabilityEdit(li.liabilityId, 'monthsRemaining', parseInt(e.target.value) || null)} placeholder="—" slotProps={{ htmlInput: { style: { textAlign: 'center' } } }} /></TableCell>
                  <TableCell align="center"><Checkbox size="small" checked={getLiabilityValue(li, 'toBePaidOff') || false} onChange={(e) => handleLiabilityEdit(li.liabilityId, 'toBePaidOff', e.target.checked)} /></TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {hasEdits && (
                        <IconButton size="small" color="primary" onClick={() => handleSaveLiability(li)} disabled={isSaving} title="Save changes">
                          {isSaving ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                        </IconButton>
                      )}
                      <IconButton size="small" color="error" onClick={() => handleDeleteLiability(li)} disabled={isSaving} title="Delete liability"><DeleteIcon fontSize="small" /></IconButton>
                    </Stack>
                    {error && <Typography variant="caption" color="error">{error}</Typography>}
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow key={li.liabilityId || i} sx={li.excludedFromDti ? { opacity: 0.5 } : undefined}>
                  <TableCell><Chip label={LIABILITY_TYPES.find(t => t.value === li.liabilityType)?.label || li.liabilityType || '—'} size="small" variant="outlined" color="warning" /></TableCell>
                  <TableCell>{li.creditorName || '—'}</TableCell>
                  <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{li.accountIdentifier || '—'}</Typography></TableCell>
                  <TableCell align="right">{fmt(li.unpaidBalance)}</TableCell>
                  <TableCell align="right">{fmt(li.monthlyPayment)}</TableCell>
                  <TableCell align="center">{li.monthsRemaining ?? '—'}</TableCell>
                  <TableCell align="center">{li.toBePaidOff ? <Chip label="Yes" size="small" color="success" variant="outlined" /> : '—'}</TableCell>
                </TableRow>
              );
            })}
            {/* New liability rows */}
            {isEditMode && newLiabilities.map((nl, idx) => {
              const tempId = `new-${idx}`;
              const isSaving = savingIds.has(tempId);
              const error = liabilityErrors[tempId];
              return (
                <TableRow key={tempId} sx={{ bgcolor: 'action.hover' }}>
                  <TableCell>
                    <TextField select size="small" value={nl.liabilityType || ''} onChange={(e) => handleNewLiabilityChange(idx, 'liabilityType', e.target.value)} sx={{ minWidth: 130 }}>
                      <MenuItem value="">Select...</MenuItem>
                      {LIABILITY_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell><TextField size="small" value={nl.creditorName || ''} onChange={(e) => handleNewLiabilityChange(idx, 'creditorName', e.target.value)} placeholder="Creditor" /></TableCell>
                  <TableCell><TextField size="small" value={nl.accountIdentifier || ''} onChange={(e) => handleNewLiabilityChange(idx, 'accountIdentifier', e.target.value)} placeholder="Acct #" /></TableCell>
                  <TableCell><CurrencyInput size="small" prefix="" value={nl.unpaidBalance ?? ''} onChange={(v) => handleNewLiabilityChange(idx, 'unpaidBalance', typeof v === 'number' ? v : null)} placeholder="0" sx={{ '& input': { textAlign: 'right' } }} /></TableCell>
                  <TableCell><CurrencyInput size="small" prefix="" value={nl.monthlyPayment ?? ''} onChange={(v) => handleNewLiabilityChange(idx, 'monthlyPayment', typeof v === 'number' ? v : null)} placeholder="0" sx={{ '& input': { textAlign: 'right' } }} /></TableCell>
                  <TableCell><TextField size="small" type="number" value={nl.monthsRemaining ?? ''} onChange={(e) => handleNewLiabilityChange(idx, 'monthsRemaining', parseInt(e.target.value) || null)} placeholder="—" slotProps={{ htmlInput: { style: { textAlign: 'center' } } }} /></TableCell>
                  <TableCell />
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton size="small" color="success" onClick={() => handleSaveNewLiability(idx)} disabled={isSaving || !nl.liabilityType} title="Save new liability">
                        {isSaving ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleRemoveNewRow(idx)} disabled={isSaving} title="Discard"><CloseIcon fontSize="small" /></IconButton>
                    </Stack>
                    {error && <Typography variant="caption" color="error">{error}</Typography>}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={3}><Typography fontWeight={700}>Total Liabilities</Typography></TableCell>
              <TableCell align="right"><Typography fontWeight={700} color="error.main">{fmt(totalLiabilityBalance)}</Typography></TableCell>
              <TableCell align="right"><Typography fontWeight={700} color="error.main">{fmt(totalLiabilityPayment)}/mo</Typography></TableCell>
              <TableCell colSpan={isEditMode ? 3 : 2} />
            </TableRow>
          </TableFooter>
        </Table>
      )}
      {isEditMode && (
        <Button size="small" variant="outlined" startIcon={<AddIcon />} sx={{ mt: 1 }} onClick={handleAddNewRow}>Add Liability</Button>
      )}

      {/* Key Ratios */}
      <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 4, mb: 2 }}>Key Ratios</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="caption" color="text.secondary">Debt-to-Income (DTI)</Typography>
              <Box><Chip label={pct(l.dtiRatio)} color={l.dtiRatio > 43 ? 'error' : l.dtiRatio > 36 ? 'warning' : 'success'} variant="outlined" /></Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="caption" color="text.secondary">Loan-to-Value (LTV)</Typography>
              <Box><Chip label={pct(l.ltvRatio)} color={l.ltvRatio > 80 ? 'warning' : 'success'} variant="outlined" /></Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="caption" color="text.secondary">Combined LTV (CLTV)</Typography>
              <Box><Chip label={pct(l.cltvRatio)} color="info" variant="outlined" /></Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

        </Grid>

        {/* Right column: Credit snapshot sidebar (sticky) */}
        {showSidebar && (
          <Grid size={{ xs: 12, lg: 4 }} sx={{ display: { xs: 'none', lg: 'block' } }}>
            {creditSidebar}
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default CreditLiabilitiesTab;
